import React, { useState, useEffect, useContext } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import axiosInstance from '../../configurations/axiosInstance/AxiosInstance';
import { LoadingContext } from '../../composants/loadingContext';
import Loader from '../../composants/loader/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faBuilding, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import LexicalViewer from '../../components/editor/LexicalViewer';
import { toast } from 'react-toastify';

const Partenaires = () => {
    const { loading, setLoading } = useContext(LoadingContext);
    const [partenaires, setPartenaires] = useState([]);
    const [selectedPartenaire, setSelectedPartenaire] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        document.title = 'Nos Partenaires - YouthFP';
        fetchPartenaires();
    }, []);

    const fetchPartenaires = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance().get('public/api/partenaires/active-published');
            console.log('Partenaires fetched:', response.data);
            setPartenaires(response.data || []);
        } catch (error) {
            console.error('Erreur lors de la récupération des partenaires:', error);
            toast.error('Impossible de charger les partenaires');
            setPartenaires([]);
        }
        setLoading(false);
    };

    const handlePartenaireClick = (partenaire) => {
        setSelectedPartenaire(partenaire);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setTimeout(() => setSelectedPartenaire(null), 300);
    };

    return (
        <>
            {loading && <Loader />}

            {/* Hero Section */}
            <div className="relative bg-gradient-to-br from-purple-600 via-pink-500 to-blue-500 text-white py-20 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-6 shadow-2xl">
                            <FontAwesomeIcon icon={faHandshake} className="text-4xl" />
                        </div>
                        <h1 className="text-5xl md:text-6xl font-extrabold mb-4 tracking-tight">
                            Nos Partenaires
                        </h1>
                        <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-6">
                            Ensemble pour la santé et le bien-être des jeunes
                        </p>
                        {partenaires.length > 0 && (
                            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full">
                                <FontAwesomeIcon icon={faBuilding} className="text-yellow-300" />
                                <span className="text-lg font-semibold">{partenaires.length}</span>
                                <span className="text-white/80">partenaire{partenaires.length > 1 ? 's' : ''}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Partners Carousel */}
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Info Card */}
                    <div className="mb-12 bg-white rounded-2xl shadow-lg p-6 border-l-4 border-purple-500">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                                <FontAwesomeIcon icon={faInfoCircle} className="text-white text-xl" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-800 mb-2">
                                    Découvrez nos partenaires
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    Cliquez sur le logo d'un partenaire pour en savoir plus sur leur contribution à notre mission.
                                </p>
                            </div>
                        </div>
                    </div>

                    {partenaires.length === 0 && !loading ? (
                        <div className="text-center py-20">
                            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-6">
                                <FontAwesomeIcon icon={faHandshake} className="text-5xl text-purple-500" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-3">
                                Aucun partenaire pour le moment
                            </h3>
                            <p className="text-gray-600">
                                Nous travaillons activement à développer nos partenariats.
                            </p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                            <Swiper
                                modules={[Autoplay, Pagination, Navigation]}
                                spaceBetween={30}
                                slidesPerView={4}
                                loop={partenaires.length > 4}
                                autoplay={{
                                    delay: 3000,
                                    disableOnInteraction: false,
                                    pauseOnMouseEnter: true,
                                }}
                                pagination={{
                                    clickable: true,
                                    dynamicBullets: true,
                                }}
                                navigation={true}
                                breakpoints={{
                                    320: { slidesPerView: 1, spaceBetween: 20 },
                                    640: { slidesPerView: 2, spaceBetween: 25 },
                                    768: { slidesPerView: 3, spaceBetween: 30 },
                                    1024: { slidesPerView: 4, spaceBetween: 30 },
                                }}
                                className="partenaires-swiper"
                            >
                                {partenaires.map((partenaire) => (
                                    <SwiperSlide key={partenaire.id}>
                                        <div
                                            onClick={() => handlePartenaireClick(partenaire)}
                                            className="group cursor-pointer transform transition-all duration-300 hover:scale-105"
                                        >
                                            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-purple-300 h-full flex flex-col items-center justify-center">
                                                <div className="relative w-full h-40 mb-4 flex items-center justify-center overflow-hidden rounded-xl bg-white p-4">
                                                    <img
                                                        src={`${partenaire.imageBase64}`}
                                                        alt={partenaire.nom}
                                                        className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                                                        onError={(e) => {
                                                            e.target.src = '/images/img.jpg';
                                                        }}
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                                                </div>
                                                <h3 className="text-lg font-bold text-gray-800 text-center mb-2 group-hover:text-purple-600 transition-colors duration-300">
                                                    {partenaire.nom}
                                                </h3>
                                                <div className="flex items-center gap-2 text-sm text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <FontAwesomeIcon icon={faInfoCircle} />
                                                    <span className="font-medium">En savoir plus</span>
                                                </div>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    )}

                    {/* Stats Cards */}
                    {partenaires.length > 0 && (
                        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white rounded-xl shadow-md p-6 text-center border-t-4 border-purple-500">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                                    <FontAwesomeIcon icon={faHandshake} className="text-3xl text-purple-600" />
                                </div>
                                <h3 className="text-3xl font-bold text-gray-800 mb-2">{partenaires.length}</h3>
                                <p className="text-gray-600 font-medium">Partenaires</p>
                            </div>

                            <div className="bg-white rounded-xl shadow-md p-6 text-center border-t-4 border-pink-500">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
                                    <FontAwesomeIcon icon={faBuilding} className="text-3xl text-pink-600" />
                                </div>
                                <h3 className="text-3xl font-bold text-gray-800 mb-2">100%</h3>
                                <p className="text-gray-600 font-medium">Engagement</p>
                            </div>

                            <div className="bg-white rounded-xl shadow-md p-6 text-center border-t-4 border-blue-500">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                                    <FontAwesomeIcon icon={faHandshake} className="text-3xl text-blue-600" />
                                </div>
                                <h3 className="text-3xl font-bold text-gray-800 mb-2">∞</h3>
                                <p className="text-gray-600 font-medium">Opportunités</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && selectedPartenaire && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    onClick={closeModal}
                >
                    <div
                        className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fadeIn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="relative bg-gradient-to-br from-purple-600 via-pink-500 to-blue-500 text-white p-6">
                            <button
                                onClick={closeModal}
                                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>

                            <div className="flex items-center gap-6">
                                <div className="w-24 h-24 bg-white rounded-2xl shadow-xl flex items-center justify-center p-3 flex-shrink-0">
                                    <img
                                        src={`${selectedPartenaire.imageBase64}`}
                                        alt={selectedPartenaire.nom}
                                        className="max-w-full max-h-full object-contain"
                                        onError={(e) => {
                                            e.target.src = '/images/img.jpg';
                                        }}
                                    />
                                </div>
                                <div>
                                    <h2 className="text-3xl font-extrabold mb-2">
                                        {selectedPartenaire.nom}
                                    </h2>
                                    <div className="flex items-center gap-2 text-white/80">
                                        <FontAwesomeIcon icon={faBuilding} />
                                        <span>Partenaire officiel</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                            <div className="mb-4">
                                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faInfoCircle} className="text-purple-500" />
                                    À propos
                                </h3>
                            </div>

                            {selectedPartenaire.description ? (
                                <LexicalViewer
                                    content={selectedPartenaire.description}
                                    showCard={false}
                                    className="prose max-w-none"
                                />
                            ) : (
                                <div className="text-center py-12 bg-gray-50 rounded-xl">
                                    <FontAwesomeIcon icon={faInfoCircle} className="text-4xl text-gray-400 mb-3" />
                                    <p className="text-gray-600">
                                        Aucune description disponible pour ce partenaire.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                            <button
                                onClick={closeModal}
                                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .partenaires-swiper {
                    padding: 40px 10px 60px;
                }

                .partenaires-swiper .swiper-button-next,
                .partenaires-swiper .swiper-button-prev {
                    color: #9333ea;
                    background: white;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }

                .partenaires-swiper .swiper-button-next:after,
                .partenaires-swiper .swiper-button-prev:after {
                    font-size: 18px;
                }

                .partenaires-swiper .swiper-pagination-bullet {
                    background: #9333ea;
                    width: 10px;
                    height: 10px;
                }

                .partenaires-swiper .swiper-pagination-bullet-active {
                    background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%);
                    width: 24px;
                    border-radius: 5px;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
        </>
    );
};

export default Partenaires;
